import { QuestionType } from '@evaltick/shared';

/** Brand tints for the type chip (Behavioural/Situational) — from the app's core UI palette. */
export type TypeChipTint = 'purple' | 'periwinkle';

/**
 * Fixed categorical palette for competency chips, matching the reference
 * design's `.chip` colors (evaltick-packages-views-v1.netlify.app) — ten
 * hand-picked hues distinct from the type-chip tints and from each other.
 * This is a deliberate exception to the app's 8-family brand palette: a
 * categorical tag palette needs more distinguishable hues than the core UI
 * palette provides. The real competency bank has more entries than tints,
 * so a competency's tint is picked by a stable hash over this list rather
 * than a 1:1 name lookup — same competency always gets the same tint, tints
 * repeat past the last one.
 */
export type CompetencyChipTint = 'c0' | 'c1' | 'c2' | 'c3' | 'c4' | 'c5' | 'c6' | 'c7' | 'c8' | 'c9';

export type ChipTint = TypeChipTint | CompetencyChipTint;

const COMPETENCY_CHIP_TINTS: CompetencyChipTint[] = ['c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8', 'c9'];

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

export const competencyChipColor = (competencyId: string): CompetencyChipTint =>
  COMPETENCY_CHIP_TINTS[hashString(competencyId) % COMPETENCY_CHIP_TINTS.length];

/** Behavioural = purple, Situational = periwinkle — fixed, not hashed. */
export const typeChipColor = (questionType: QuestionType): TypeChipTint =>
  questionType === QuestionType.SITUATIONAL ? 'periwinkle' : 'purple';
